import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Loading from '../Common/Loading';
import CustomAlert from '../Common/CustomAlert';
import go_n from '../Common/Images/go_n.gif';
import go_o from '../Common/Images/go_o.gif';
import close_n from '../Common/Images/cancel_n.gif';
import close_o from '../Common/Images/cancel_o.gif';
import popimg from "../Common/Images/ImagesForAlert/popup_bg1.png";
import popbg from "../Common/Images/ImagesForAlert/popup_bg2.png";
import popclose from "../Common/Images/ImagesForAlert/popup_close_n.png";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface SupportEntryRecord {
    SchoolCode: string;
    FamilyCode: string;
    Login: string;
    Surname: string;
    Address: string;
    Suburb: string;
    Postcode: string;
    Phone: string;
    DateOfSale: string;
    SalesOffice: string;
    Email: string;
    FromLogin: string;
    ToLogin: string;
    ActionDate: string;
    ActionCompletionDate: string;
    TypeOfIssue: string;
    ReasonType: string;
    TimeSpent: string;
    State: string;
}

interface ProblemType {
    minor: string;
    major: string;
    properties: string;
}

const styles = {
    container: {
        position: 'absolute' as 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column' as 'column',
        fontFamily: 'Verdana, Arial, Helvetica, sans-serif', fontSize: '11px',
        backgroundColor: 'azure', overflow: 'hidden'
    },
    headerSection: {
        backgroundColor: 'azure',
        borderBottom: '1px solid #80a0ff',
        flexShrink: 0,
        padding: '10px',
        textAlign: 'center' as 'center'
    },
    filterTable: {
        width: '100%',
        borderCollapse: 'collapse' as const,
        backgroundColor: 'azure',
        border: '1px solid blue'
    },
    tableContainer: {
        flex: 1,
        overflow: 'auto',
        backgroundColor: 'azure'
    },
    dataTable: {
        width: '100%',
        borderCollapse: 'collapse' as 'collapse',
        fontSize: '11px',
        border: '1px solid #80a0ff'
    },
    th: {
        fontFamily: 'Arial',
        backgroundColor: '#C7DDFD',
        color: '#2859ac',
        fontSize: '10pt',
        padding: '8px',
        fontWeight: 'normal',
        border: '1px solid #80a0ff',
        textAlign: 'left' as 'left',
        position: 'sticky' as 'sticky',
        top: 0,
        zIndex: 1
    },
    td: {
        padding: '8px',
        fontFamily: 'Arial',
        fontSize: '10pt',
        borderRight: '1px solid #80a0ff',
        textWrap:"nowrap"
    },
    input: {
        fontFamily: 'Verdana, Arial',
        fontSize: '11px',
        padding: '1px',
        border: '1px solid #80a0ff',
        marginRight: '5px'
    },
    footer: {
        marginTop: 'auto',
        marginBottom: '10px',
        backgroundColor: '#C7DDFD',
        padding: '8px 10px',
        borderTop: '1px solid #80a0ff',
        borderBottom: '1px solid #80a0ff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontFamily: 'Arial',
        fontSize: '10pt',
        color: '#2859ac',
        fontWeight: 'bold' as 'bold',
        flexShrink: 0,
    },
    navLink: {
        cursor: 'pointer',
        margin: '0 8px',
        userSelect: 'none' as 'none',
        color: '#2859ac',
        textDecoration: 'underline'
    },
    refreshLink: {
        color: 'blue',
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 'normal' as 'normal',
        marginRight: '20px',
        marginLeft: '5px'
    }
};

const EL_CustomerSupportEntryDetails: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const sParam = searchParams.get('S') || '';
    const fParam = searchParams.get('F') || '';

    const [countries, setCountries] = useState<string[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<string>('');

    const [schools, setSchools] = useState<{ SchoolCode: string, Name: string }[]>([]);
    const [selectedSchoolCode, setSelectedSchoolCode] = useState<string>('');

    const [tutors, setTutors] = useState<string[]>([]);
    const [selectedFromTutor, setSelectedFromTutor] = useState<string>('');
    const [selectedToTutor, setSelectedToTutor] = useState<string>('');

    const [problemTypes, setProblemTypes] = useState<ProblemType[]>([]);
    const [selectedProbType, setSelectedProbType] = useState<string>('');

    const [reasons, setReasons] = useState<string[]>([]);
    const [selectedReason, setSelectedReason] = useState<string>('');

    const [stateFilter, setStateFilter] = useState<string>('');

    const defaultFromDate = new Date();
    defaultFromDate.setMonth(defaultFromDate.getMonth() - 1);
    defaultFromDate.setDate(1);
    const [fromDate, setFromDate] = useState<string>(defaultFromDate.toISOString().split('T')[0]);
    const [toDate, setToDate] = useState<string>(new Date().toISOString().split('T')[0]);

    const [tableData, setTableData] = useState<SupportEntryRecord[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [activeRowId, setActiveRowId] = useState<string | null>(null);

    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [tempRowsInput, setTempRowsInput] = useState(rowsPerPage.toString());

    const [isGoHovered, setIsGoHovered] = useState(false);
    const [isCancelHovered, setIsCancelHovered] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const [alertState, setAlertState] = useState({ show: false, message: '', isSuccess: false });

    const showAlert = (msg: string, isSuccess: boolean = false) => setAlertState({ show: true, message: msg, isSuccess });
    const handleCloseAlert = () => setAlertState(prev => ({ ...prev, show: false }));
    useEffect(() => {
        const fetchCountries = async () => {
            setLoading(true);

            try {
                const response = await fetch(`${API_BASE_URL}api/get_countries/`);

                if (response.ok) {
                    const data = await response.json();
                    const apiCountries =
                        data.countries ||
                        data.map((item: any) => item.major || item.Country || item);

                    setCountries(apiCountries);
                }
            } catch (err) {
                console.error("Failed to fetch countries:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchCountries();
    }, []);

    useEffect(() => {
        const fetchSchools = async () => {
            setSchools([]);
            setSelectedSchoolCode('');

            if (!selectedCountry || selectedCountry === '-Select-') return;

            setLoading(true);

            try {
                const response = await fetch(
                    `${API_BASE_URL}api/get_schools/?country=${encodeURIComponent(selectedCountry)}&category=DLR`
                );

                if (response.ok) {
                    const data = await response.json();
                    setSchools(data.schools || data);
                }
            } catch (err) {
                console.error("Failed to fetch schools:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchSchools();
    }, [selectedCountry]);

    useEffect(() => {
        const fetchTutorsAndTypes = async () => {
            setTutors([]);
            setProblemTypes([]);
            setSelectedFromTutor('');
            setSelectedToTutor('');
            setSelectedProbType('');

            if (!selectedSchoolCode || selectedSchoolCode === '-Select-') return;

            setLoading(true);

            try {
                const tutorRes = await fetch(
                    `${API_BASE_URL}api/get_tutors/?school_code=${encodeURIComponent(selectedSchoolCode)}`
                );

                if (tutorRes.ok) {
                    const tData = await tutorRes.json();
                    setTutors(tData.tutors || tData);
                }

                const probRes = await fetch(
                    `${API_BASE_URL}api/get_problem_types/`
                );

                if (probRes.ok) {
                    const pData = await probRes.json();
                    setProblemTypes(pData.types || pData);
                }
            } catch (err) {
                console.error("Failed to fetch Tutors/Types:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchTutorsAndTypes();
    }, [selectedSchoolCode]);

    useEffect(() => {
        setReasons([]);
        setSelectedReason('');

        if (!selectedProbType) return;

        const selectedType = problemTypes.find(p => p.minor === selectedProbType);
        if (selectedType && selectedType.properties) {
            const reasonsArray = selectedType.properties.split(',').map(r => r.trim());
            setReasons(reasonsArray);
        }
    }, [selectedProbType, problemTypes]);

    const handleGoClick = async () => {



        if (!selectedSchoolCode) {
            showAlert("Please select Distributor Code");
            return;
        }
        setHasSearched(true);
        setLoading(true);
        setCurrentPage(1);
        setActiveRowId(null);

        try {
            const params = new URLSearchParams({
                schoolCode: selectedSchoolCode,
                fromTutor: selectedFromTutor,
                toTutor: selectedToTutor,
                probType: selectedProbType,
                reason: selectedReason,
                state: stateFilter,
                fromDate: fromDate,
                toDate: toDate
            });

            const response = await fetch(`${API_BASE_URL}api/get_customer_support_entries/?${params.toString()}`);

            if (!response.ok) {
                throw new Error(`HTTP Error: ${response.status}`);
            }

            const result = await response.json();

            if (result.error) {
                throw new Error(result.error);
            }

            setTableData(result.data || []);
        } catch (error: any) {
            console.error("Fetch Error:", error);
            showAlert(error.message || "Error loading support entry data");
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = (record: SupportEntryRecord, index: number) => {
        const rowId = `${record.SchoolCode}-${record.FamilyCode}-${record.Login}-${index}`;
        setActiveRowId(rowId);

        const sfl = sParam && fParam ? `&S=${sParam}&F=${fParam}` : '';
        const url = `/SUPPORT/EL_AddEditTask?FamilyCode=${encodeURIComponent(record.FamilyCode)}&SchoolCode=${encodeURIComponent(record.SchoolCode)}&Login=${encodeURIComponent(record.Login)}&FilterType=CustEntry${sfl}`;

        window.open(url, '_blank');
    };

    const totalPages = Math.ceil(tableData.length / rowsPerPage);
    const indexOfLastItem = currentPage * rowsPerPage;
    const indexOfFirstItem = indexOfLastItem - rowsPerPage;
    const currentItems = tableData.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (direction: 'prev' | 'next' | 'first' | 'last') => {
        switch (direction) {
            case 'prev': setCurrentPage(prev => Math.max(prev - 1, 1)); break;
            case 'next': setCurrentPage(prev => Math.min(prev + 1, totalPages)); break;
            case 'first': setCurrentPage(1); break;
            case 'last': setCurrentPage(totalPages); break;
        }
    };

    const handleRefreshRows = () => {
        const newRows = parseInt(tempRowsInput);
        if (!isNaN(newRows) && newRows > 0) {
            setRowsPerPage(newRows);
            setCurrentPage(1);
        }
    };

    return (
        <div style={styles.container}>
            {/* Responsive-only CSS. No layout/behavior logic changed - this just
                lets the fixed-width filter row and the wide data table scroll
                horizontally on narrow screens instead of overflowing/breaking,
                and lets the header/footer wrap instead of clipping. */}
            <style>{`
                .ecs-header-title-row {
                    flex-wrap: wrap;
                    row-gap: 8px;
                }
                .ecs-filter-scroll {
                    width: 100%;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                }
                .ecs-filter-scroll table {
                    min-width: 1400px;
                }
                .ecs-table-scroll {
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                }
                .ecs-table-scroll table {
                    min-width: 1300px;
                }
                .ecs-footer {
                    flex-wrap: wrap;
                    row-gap: 8px;
                }
                @media (max-width: 768px) {
                    .ecs-header-title-row {
                        font-size: 14px !important;
                        gap: 8px !important;
                    }
                    .ecs-admin-bar span {
                        font-size: 10pt !important;
                    }
                    .ecs-footer {
                        justify-content: center !important;
                        text-align: center;
                    }
                    .ecs-footer > div {
                        width: 100%;
                        justify-content: center !important;
                    }
                }
                @media (max-width: 480px) {
                    .ecs-admin-bar {
                        padding: 10px 0 !important;
                    }
                }
            `}</style>

            <div
                className="ecs-admin-bar"
                style={{
                    backgroundImage: `url(${popimg}), url(${popbg})`,
                    backgroundRepeat: "no-repeat, no-repeat",
                    backgroundPosition: "left center, right center",
                    backgroundSize: "30% 100%, 70% 100%",
                    height: "16px",
                    color: "white",
                    padding: "7px 0px",
                    fontWeight: "bold",
                    fontSize: "12pt",
                    fontFamily: "arial",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    // marginBottom: "10px",
                }}
            >
                <span style={{ marginLeft: "10px" }}>
                    ADMIN
                </span>
                <img
                    src={popclose}
                    alt="close"
                    height="30px"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/SysAdmin")}
                />
            </div>
            {loading && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loading />
                </div>
            )}

            <div style={styles.headerSection}>
                <div
                    className="ecs-header-title-row"
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "15px",
                        fontFamily: "Arial",
                        fontSize: "16px",
                        marginBottom: "10px"
                    }}
                >
                    <b>Customer Support Entry Details</b>

                    <span>Country:</span>

                    <select
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        style={{ width: "120px" }}
                    >
                        <option value="">--Select--</option>
                        {countries.map((c, idx) => (
                            <option key={idx} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="ecs-filter-scroll">
                    <table
                        cellPadding={0}
                        cellSpacing={0}
                        style={styles.filterTable}
                    >
                        <tbody>
                            <tr>
                                <td
                                    style={{
                                        width: '240px',
                                        border: '1px solid blue',
                                        padding: '4px 8px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <label style={{ fontSize: '10pt', fontFamily: 'Arial' }}>
                                        SchoolCode:&nbsp;
                                    </label>

                                    <select
                                        value={selectedSchoolCode}
                                        onChange={(e) => setSelectedSchoolCode(e.target.value)}
                                        style={{
                                            width: '130px',
                                            fontSize: '10pt'
                                        }}
                                    >
                                        <option value="">--Select--</option>
                                        {schools.map((s, idx) => (
                                            <option key={idx} value={s.SchoolCode}>
                                                {s.Name || s.SchoolCode}
                                            </option>
                                        ))}
                                    </select>
                                </td>

                                <td
                                    style={{
                                        width: '210px',
                                        border: '1px solid blue',
                                        padding: '4px 8px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <label style={{ fontSize: '10pt', fontFamily: 'Arial' }}>
                                        FromTutor:&nbsp;
                                    </label>

                                    <select
                                        value={selectedFromTutor}
                                        onChange={(e) => setSelectedFromTutor(e.target.value)}
                                        style={{
                                            width: '120px',
                                            fontSize: '10pt'
                                        }}
                                    >
                                        <option value="">--Select--</option>
                                        {tutors.map((t, idx) => (
                                            <option key={idx} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </select>
                                </td>

                                <td
                                    style={{
                                        width: '210px',
                                        border: '1px solid blue',
                                        padding: '4px 8px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <label style={{ fontSize: '10pt', fontFamily: 'Arial' }}>
                                        To Tutor:&nbsp;
                                    </label>

                                    <select
                                        value={selectedToTutor}
                                        onChange={(e) => setSelectedToTutor(e.target.value)}
                                        style={{
                                            width: '120px',
                                            fontSize: '10pt'
                                        }}
                                    >
                                        <option value="">--Select--</option>
                                        {tutors.map((t, idx) => (
                                            <option key={idx} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </select>
                                </td>

                                <td
                                    style={{
                                        width: '200px',
                                        border: '1px solid blue',
                                        padding: '4px 8px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <label style={{ fontSize: '10pt', fontFamily: 'Arial' }}>
                                        Type:&nbsp;
                                    </label>

                                    <select
                                        value={selectedProbType}
                                        onChange={(e) => setSelectedProbType(e.target.value)}
                                        style={{
                                            width: '120px',
                                            fontSize: '10pt'
                                        }}
                                    >
                                        <option value="">--Select--</option>
                                        {problemTypes.map((p, idx) => (
                                            <option key={idx} value={p.minor}>
                                                {p.major}
                                            </option>
                                        ))}
                                    </select>
                                </td>

                                <td
                                    style={{
                                        width: '180px',
                                        border: '1px solid blue',
                                        padding: '4px 8px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <label style={{ fontSize: '10pt', fontFamily: 'Arial' }}>
                                        Reason:&nbsp;
                                    </label>

                                    <select
                                        value={selectedReason}
                                        onChange={(e) => setSelectedReason(e.target.value)}
                                        style={{
                                            width: '100px',
                                            fontSize: '10pt'
                                        }}
                                    >
                                        <option value=""></option>
                                        {reasons.map((r, idx) => (
                                            <option key={idx} value={r}>
                                                {r}
                                            </option>
                                        ))}
                                    </select>
                                </td>

                                <td
                                    style={{
                                        width: '140px',
                                        border: '1px solid blue',
                                        padding: '4px 8px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <label style={{ fontSize: '10pt', fontFamily: 'Arial' }}>
                                        State:&nbsp;
                                    </label>

                                    <input
                                        type="text"
                                        value={stateFilter}
                                        onChange={(e) => setStateFilter(e.target.value)}
                                        maxLength={10}
                                        style={{
                                            width: '70px',
                                            fontSize: '10pt'
                                        }}
                                    />
                                </td>

                                <td
                                    style={{
                                        width: '170px',
                                        border: '1px solid blue',
                                        padding: '4px 8px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <label style={{ fontSize: '10pt', fontFamily: 'Arial' }}>
                                        From&nbsp;
                                    </label>

                                    <input
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        style={{
                                            width: '125px',
                                            fontSize: '10pt'
                                        }}
                                    />
                                </td>

                                <td
                                    style={{
                                        width: '170px',
                                        border: '1px solid blue',
                                        padding: '4px 8px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <label style={{ fontSize: '10pt', fontFamily: 'Arial' }}>
                                        To&nbsp;
                                    </label>

                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        style={{
                                            width: '125px',
                                            fontSize: '10pt'
                                        }}
                                    />
                                </td>

                                <td
                                    style={{
                                        width: '190px',
                                        border: '1px solid blue',
                                        padding: '4px 8px',
                                        whiteSpace: 'nowrap',
                                        textAlign: 'center'
                                    }}
                                >
                                    <img
                                        src={isGoHovered ? go_o : go_n}
                                        alt="Go"
                                        onClick={handleGoClick}
                                        onMouseEnter={() => setIsGoHovered(true)}
                                        onMouseLeave={() => setIsGoHovered(false)}
                                        style={{
                                            cursor: 'pointer',
                                            marginRight: '15px',
                                            verticalAlign: 'middle'
                                        }}
                                    />

                                    <img
                                        src={isCancelHovered ? close_o : close_n}
                                        alt="Close"
                                        onClick={() => navigate(-1)}
                                        onMouseEnter={() => setIsCancelHovered(true)}
                                        onMouseLeave={() => setIsCancelHovered(false)}
                                        style={{
                                            cursor: 'pointer',
                                            verticalAlign: 'middle'
                                        }}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {hasSearched && (
                <div style={styles.tableContainer} className="ecs-table-scroll">
                    <table style={styles.dataTable}>
                        <thead>
                            <tr>
                                <th
                                    colSpan={17}
                                    style={{
                                        backgroundColor: "#C7DDFD",
                                        color: "#2859ac",
                                        textAlign: "center",
                                        fontWeight: "bold",
                                        fontSize: "12pt",
                                        fontFamily: "Arial",
                                        padding: "10px",
                                        border: "1px solid #80A0FF",
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 3
                                    }}
                                >
                                    Customer Support / Family Details
                                </th>
                            </tr>

                            <tr>
                                <th style={{ ...styles.th, width: '3%', textAlign: 'center' }}>#</th>
                                <th style={styles.th}>Surname</th>
                                <th style={styles.th}>Address</th>
                                <th style={styles.th}>Suburb</th>
                                <th style={styles.th}>PostCode</th>
                                <th style={styles.th}>Phone</th>
                                <th style={styles.th}>DateOfSale</th>
                                <th style={styles.th}>SalesOffice</th>
                                <th style={styles.th}>Email</th>
                                <th style={styles.th}>From</th>
                                <th style={styles.th}>To</th>
                                <th style={styles.th}>Action Date</th>
                                <th style={styles.th}>Action<br />Completion Date</th>
                                <th style={styles.th}>Type</th>
                                <th style={styles.th}>Reason</th>
                                <th style={styles.th}>Time Spent</th>
                                <th style={styles.th}>State</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length === 0 ? (
                                <tr><td colSpan={18} style={{ textAlign: 'center', padding: '20px', color: '#666' }}></td></tr>
                            ) : (
                                currentItems.map((row, idx) => {
                                    const currentRowId = `${row.SchoolCode}-${row.FamilyCode}-${row.Login}-${idx}`;
                                    const isActive = activeRowId === currentRowId;

                                    return (
                                        <tr
                                            key={idx}
                                            onClick={() => handleRowClick(row, idx)}
                                            style={{
                                                cursor: 'pointer',
                                                backgroundColor: isActive ? 'orange' : (idx % 2 === 0 ? '#F4FAFF' : 'white'),
                                                color: isActive ? 'white' : 'black',
                                                borderBottom: '1px solid #80a0ff'
                                            }}
                                        >
                                            <td style={{ ...styles.td, textAlign: 'center' }}>{indexOfFirstItem + idx + 1}</td>
                                            <td style={styles.td}>{row.Surname}</td>
                                            <td style={styles.td}>{row.Address}</td>
                                            <td style={styles.td}>{row.Suburb}</td>
                                            <td style={styles.td}>{row.Postcode}</td>
                                            <td style={styles.td}>{row.Phone}</td>
                                            <td style={styles.td}>{row.DateOfSale}</td>
                                            <td style={styles.td}>{row.SalesOffice}</td>
                                            <td style={styles.td}>{row.Email}</td>
                                            <td style={styles.td}>{row.FromLogin}</td>
                                            <td style={styles.td}>{row.ToLogin}</td>
                                            <td style={styles.td}>{row.ActionDate}</td>
                                            <td style={styles.td}>{row.ActionCompletionDate}</td>
                                            <td style={styles.td}>{row.TypeOfIssue}</td>
                                            <td style={styles.td}>{row.ReasonType}</td>
                                            <td style={styles.td}>{row.TimeSpent}</td>
                                            <td style={styles.td}>{row.State}</td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {hasSearched && tableData.length > 0 && (
                <div style={styles.footer} className="ecs-footer">
                    <div style={{ fontWeight: 'bold', fontSize: '12pt' }}>
                        *&nbsp;&nbsp; O = Old YLA Sales entered,&nbsp;&nbsp; M = Migrated from old YLA,&nbsp;&nbsp;
                        A = Registered from APP,&nbsp;&nbsp; B = Bootcamp; K =Kinetic Website
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', fontSize: "12pt" }}>
                        <span>Rows per page: </span>
                        <input
                            type="text"
                            value={tempRowsInput}
                            onChange={(e) => setTempRowsInput(e.target.value)}
                            style={{ ...styles.input, width: '40px', textAlign: 'center', marginLeft: '5px' }}
                        />
                        <span style={styles.refreshLink} onClick={handleRefreshRows}>Refresh</span>

                        <span style={styles.navLink} onClick={() => handlePageChange('first')}>&lt;&lt;</span>
                        <span style={styles.navLink} onClick={() => handlePageChange('prev')}>&lt;</span>

                        <span style={{ margin: '0 5px', fontWeight: 'normal', color: 'black' }}>
                            {currentPage}&nbsp;&nbsp;of&nbsp;&nbsp;{totalPages || 1}
                        </span>

                        <span style={styles.navLink} onClick={() => handlePageChange('next')}>&gt;</span>
                        <span style={styles.navLink} onClick={() => handlePageChange('last')}>&gt;&gt;</span>
                    </div>
                </div>
            )}

            {alertState.show && (
                <CustomAlert message={alertState.message} onClose={handleCloseAlert} />
            )}
        </div>
    );
};

export default EL_CustomerSupportEntryDetails;